import React from 'react';
import { TableCell, TableRow, Typography } from '@mui/material';
import {
  getLegacyDynamicColumns,
  getStoreConfigurationFlags
} from './utils/legacyAuditStoreConfiguration';

const inventoryColumns = ['Opening', 'Activated', 'Sales', 'Packs Moved-To Backstock', 'Closing', 'Sales $'];

type LegacyAuditTableHeaderProps = {
  selectedStore?: any;
};

const subheadingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '18px',
  letterSpacing: 0,
  color: '#222B45',
};

const headingSx = {
  ...subheadingSx,
  fontWeight: 600,
  fontSize: '15px',
  lineHeight: '18px',
};

const verticalHeaderTextSx = {
  display: 'inline-flex',
  flexDirection: 'column',
  textAlign: 'center',
  transform: 'rotate(180deg)',
  whiteSpace: 'pre-line',
  width: '100%',
  writingMode: 'vertical-rl',
  justifyContent: 'flex-end'
} as const;

const LegacyAuditTableHeader: React.FC<LegacyAuditTableHeaderProps> = ({ selectedStore }) => {
  const groupBorderStyle = { borderRight: '1px solid #A3AED0CC' };
  const innerBorderStyle = { borderRight: '1px solid #A3AED03D' };
  const auditColumns = getLegacyDynamicColumns(selectedStore);
  const {
    isBothConnected,
    isBothNotConnected,
    isLottoConnectedOnly,
    isPOSConnectedOnly
  } = getStoreConfigurationFlags(selectedStore);
  
  return (
    <>
      <TableRow sx={{ backgroundColor: '#F3F2F7' }}>
        <TableCell
          rowSpan={2}
          sx={{
            width: 280,
            minWidth: 280,
            maxWidth: 280,
            position: 'sticky',
            left: 0,
            top: 0,
            zIndex: 2,
            ...groupBorderStyle,
          }}
        >
          <Typography variant="caption" sx={headingSx}>
            Date
          </Typography>
        </TableCell>
        <TableCell
          rowSpan={2}
          sx={{
            width: 40,
            minWidth: 40,
            top: 0,
            position: 'sticky',
            zIndex: 1,
            textAlign: 'right',
            ...groupBorderStyle,
          }}
        >
          <Typography variant="caption" sx={headingSx}>
            # of Audits
          </Typography>
        </TableCell>
        <TableCell colSpan={inventoryColumns.length} sx={{ textAlign: 'center', ...groupBorderStyle }}>
          <Typography variant="caption" sx={headingSx}>
            Inventory
          </Typography>
        </TableCell>
        {isBothConnected && (
          <TableCell colSpan={auditColumns.length} sx={{ textAlign: 'center', ...groupBorderStyle }}>
            <Typography variant="caption" sx={headingSx}>
              Audit Report
            </Typography>
          </TableCell>
        )}
        {isBothNotConnected && (
          <TableCell colSpan={auditColumns.length} sx={{ textAlign: 'center', ...groupBorderStyle }}>
            <Typography variant="caption" sx={headingSx}>
              Scratcher Summary
            </Typography>
          </TableCell>
        )}
        {isLottoConnectedOnly && (
          <>
            <TableCell colSpan={2} sx={{ textAlign: 'center', ...groupBorderStyle }}>
              <Typography variant="caption" sx={headingSx}>
                Scratcher Summary
              </Typography>
            </TableCell>
            <TableCell colSpan={2} sx={{ textAlign: 'center', ...groupBorderStyle }}>
              <Typography variant="caption" sx={headingSx}>
                Lotto Summary
              </Typography>
            </TableCell>
          </>
        )}
        {isPOSConnectedOnly && (
          <>
            <TableCell colSpan={2} sx={{ textAlign: 'center', ...groupBorderStyle }}>
              <Typography variant="caption" sx={headingSx}>
                Scratcher Summary
              </Typography>
            </TableCell>
            <TableCell colSpan={2} sx={{ textAlign: 'center', ...groupBorderStyle }}>
              <Typography variant="caption" sx={headingSx}>
                POS(Lotto) Summary
              </Typography>
            </TableCell>
          </>
        )}
        <TableCell rowSpan={2} sx={{ width: 140, textAlign: 'center' }}>
          <Typography variant="caption" sx={headingSx}>
            Reconcile<br/>
          </Typography>
          <Typography variant="caption" sx={subheadingSx}>
            Discrepancy / Reconciliation
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow sx={{
        '& .MuiTableCell-root': {
          borderBottom: '1px solid #A3AED0CC',
        },
      }}>
        {inventoryColumns.map((label, index) => {
          const isGroupEnd = index === inventoryColumns.length - 1;
          const border = isGroupEnd ? groupBorderStyle : innerBorderStyle;
          return (
          <TableCell 
            key={label} 
            sx={{ 
              textAlign: 'right',
              ...border,
            }}
          >
            <Typography variant="caption" sx={{ ...subheadingSx, ...verticalHeaderTextSx }}>
              {label.split(' ').join('\n')}
            </Typography>
          </TableCell>
        );
        })}
        {auditColumns.map((label, index) => {
          const isGroupEnd = index === auditColumns.length - 1;
          const border = isGroupEnd ? groupBorderStyle : innerBorderStyle;
          return (
          <TableCell 
            key={label} 
            sx={{ 
              textAlign: 'right',
              ...border,
            }}
          >
            <Typography variant="caption" sx={{ ...subheadingSx, ...verticalHeaderTextSx }}>
              {label.split(' ').join('\n')}
            </Typography>
          </TableCell>
        );
        })}
      </TableRow>
    </>
  );
};

export default LegacyAuditTableHeader;
